import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { versionDescription_softwareReleasesVersionDetail$key } from '@atlassian/jira-relay/src/__generated__/versionDescription_softwareReleasesVersionDetail.graphql';
import {
	CollapsibleSection,
	CollapsibleToggleButton,
} from '@atlassian/jira-software-releases-collapsible-ui-toggle';
import { AsyncEditableVersionDescription as EditableDescription } from './editable-version-description/async';
import messages from './messages';

export const VersionDescription = ({
	jiraVersion,
}: {
	jiraVersion: versionDescription_softwareReleasesVersionDetail$key;
}) => {
	const { formatMessage } = useIntl();
	const version = useFragment(
		graphql`
			fragment versionDescription_softwareReleasesVersionDetail on JiraVersion {
				...async_softwareReleasesVersionDetail_AsyncEditableVersionDescription
				...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
			}
		`,
		jiraVersion,
	);

	return (
		<CollapsibleSection
			jiraVersion={version}
			uiType="DESCRIPTION"
			renderHeader={(isCollapsed, onToggleClicked) => (
				<HeaderWrapper>
					<HeaderLeftClickable onClick={onToggleClicked}>
						<Heading size="small" as="h2">
							{formatMessage(messages.descriptionHeader)}
							<CollapsibleToggleButton isCollapsed={isCollapsed} />
						</Heading>
					</HeaderLeftClickable>
				</HeaderWrapper>
			)}
			renderBody={() => (
				<DescriptionContainer data-testid="software-releases-version-detail.ui.version-description.description-container">
					<Box xcss={inlinedEditOffsetStyles}>
						<EditableDescription jiraVersion={version} shouldTruncateText={false} />
					</Box>
				</DescriptionContainer>
			)}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200', '16px'),
});

const inlinedEditOffsetStyles = xcss({
	marginTop: 'space.negative.100',
	marginLeft: 'space.negative.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderLeftClickable = styled.div({
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
});
